import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Clock, Plus, Trash2, Wrench } from "lucide-react";

const STATUS_META = {
  REQUESTED: "bg-sky-100 text-sky-900",
  ASSIGNED: "bg-blue-100 text-blue-900",
  ACCEPTED: "bg-blue-100 text-blue-900",
  DECLINED_BY_TECH: "bg-amber-100 text-amber-900",
  ON_VISIT: "bg-indigo-100 text-indigo-900",
  ESTIMATED: "bg-amber-100 text-amber-900",
  APPROVED_BY_CUSTOMER: "bg-indigo-100 text-indigo-900",
  REJECTED_BY_CUSTOMER: "bg-rose-100 text-rose-900",
  COMPLETED: "bg-emerald-100 text-emerald-900",
  CANCELLED: "bg-slate-100 text-slate-700",
  DISPUTED: "bg-rose-200 text-rose-900",
};
function Status({ s }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_META[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
}

export default function VendorPilotBookings() {
  const [bookings, setBookings] = useState(null);
  const [current, setCurrent] = useState(null);
  const [estDialog, setEstDialog] = useState(false);
  const [est, setEst] = useState({
    visiting_fee: "150", visiting_fee_adjustment: "150",
    labour: "", taxes: "0", notes: "", materials: [],
  });

  const refresh = async () => {
    try {
      const { data } = await api.get("/vendor/pilot/bookings");
      setBookings(data.bookings || []);
    } catch {
      setBookings([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const act = async (path, body, onOk) => {
    try {
      await api.post(path, body || {});
      toast.success(onOk || "Done");
      await refresh();
      if (current) {
        const { data } = await api.get(`/vendor/pilot/bookings`).catch(() => ({ data: {} }));
        const fresh = (data?.bookings || []).find((b) => b.booking_id === current.booking_id);
        if (fresh) setCurrent(fresh);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const accept = (b) => act(`/vendor/pilot/bookings/${b.booking_id}/accept`, {}, "Accepted");
  const decline = (b) => {
    const reason = window.prompt("Reason for decline?");
    if (reason === null) return;
    act(`/vendor/pilot/bookings/${b.booking_id}/decline`, { reason }, "Declined");
  };
  const onVisit = (b) => act(`/vendor/pilot/bookings/${b.booking_id}/on-visit`, {}, "On visit");
  const complete = (b) => act(`/vendor/pilot/bookings/${b.booking_id}/complete`, {}, "Completed");

  const addMaterial = () => setEst({ ...est, materials: [...est.materials, { name: "", qty: "1", unit_price: "0" }] });
  const updMaterial = (i, k, v) => setEst({ ...est, materials: est.materials.map((m, idx) => idx === i ? { ...m, [k]: v } : m) });
  const rmMaterial = (i) => setEst({ ...est, materials: est.materials.filter((_, idx) => idx !== i) });

  const postEstimate = async () => {
    if (!current) return;
    if (!est.labour) return toast.error("Labour required (enter 0 if walk-away)");
    try {
      await api.post(`/vendor/pilot/bookings/${current.booking_id}/estimate`, est);
      toast.success("Estimate posted");
      setEstDialog(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post estimate"); }
  };

  if (bookings === null) return <Loading />;

  return (
    <div className="space-y-6" data-testid="vendor-pilot-bookings">
      <PageHeader title="Pilot Bookings" subtitle="Boisar resident technician assignments. Preview flow — no payment collected, no payout initiated." />

      {bookings.length === 0 ? (
        <Card><CardContent className="pt-6 text-sm text-slate-500">No pilot bookings assigned to you yet.</CardContent></Card>
      ) : (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Your bookings</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm border">
              <thead className="bg-slate-50"><tr>
                <th className="p-2 text-left">Created</th><th className="p-2 text-left">Category</th>
                <th className="p-2 text-left">Resident</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Actions</th>
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-t">
                    <td className="p-2 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="p-2 text-xs">{b.category}</td>
                    <td className="p-2 text-xs">{b.resident_display || b.resident_email}</td>
                    <td className="p-2"><Status s={b.status} /></td>
                    <td className="p-2 text-xs flex gap-1 flex-wrap">
                      {b.status === "ASSIGNED" && (<>
                        <Button size="sm" onClick={() => accept(b)} data-testid={`vendor-pbk-accept-${b.booking_id}`}>Accept</Button>
                        <Button variant="outline" size="sm" onClick={() => decline(b)} data-testid={`vendor-pbk-decline-${b.booking_id}`}>Decline</Button>
                      </>)}
                      {b.status === "ACCEPTED" && <Button size="sm" onClick={() => onVisit(b)} data-testid={`vendor-pbk-onvisit-${b.booking_id}`}>Mark on-visit</Button>}
                      {["ON_VISIT", "REJECTED_BY_CUSTOMER"].includes(b.status) && (
                        <Button size="sm" onClick={() => { setCurrent(b); setEstDialog(true); }} data-testid={`vendor-pbk-estimate-${b.booking_id}`}>Post estimate</Button>
                      )}
                      {b.status === "APPROVED_BY_CUSTOMER" && <Button size="sm" onClick={() => complete(b)} data-testid={`vendor-pbk-complete-${b.booking_id}`}>Complete</Button>}
                      <Button variant="outline" size="sm" onClick={() => setCurrent(b)} data-testid={`vendor-pbk-open-${b.booking_id}`}>Open</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Detail dialog */}
      <Dialog open={!!current && !estDialog} onOpenChange={(o) => !o && setCurrent(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Booking · {current?.category}</DialogTitle></DialogHeader>
          {current && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><Status s={current.status} /><span className="text-xs text-slate-500">{current.booking_id}</span></div>
              <div className="rounded-md border p-3 space-y-1">
                <div><b>Resident:</b> {current.resident_display} · {current.resident_phone}</div>
                <div><b>Address:</b> {current.resident_address}</div>
                <div><b>Description:</b> {current.description}</div>
                <div><b>Preferred slot:</b> {current.preferred_slot}</div>
              </div>
              {current.estimate && (
                <div className="rounded-md border p-3 space-y-1">
                  <div className="font-medium">Posted estimate</div>
                  <div>Net visiting ₹{current.estimate.net_visiting_charged} + Labour ₹{current.estimate.labour} + Materials ₹{current.estimate.materials_total} + Taxes ₹{current.estimate.taxes} = <b>₹{current.estimate.grand_total}</b></div>
                  <div className="text-xs text-slate-600">Commission ₹{current.estimate.commission.commission_amount} · your share ₹{current.estimate.commission.technician_share} + ₹{current.estimate.materials_total} materials reimbursement (preview only).</div>
                </div>
              )}
              <div className="rounded-md border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900 flex gap-2 items-start"><Wrench className="h-4 w-4 mt-0.5" /><span>Preview — no real credit, no withdrawal. Earnings appear in the Super Admin ledger as informational entries only.</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Estimate dialog */}
      <Dialog open={estDialog} onOpenChange={setEstDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Post itemized estimate</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Visiting fee (₹)</Label><Input value={est.visiting_fee} onChange={(e) => setEst({ ...est, visiting_fee: e.target.value })} data-testid="vendor-est-visiting" /></div>
              <div><Label>Visiting fee adjustment (₹)</Label><Input value={est.visiting_fee_adjustment} onChange={(e) => setEst({ ...est, visiting_fee_adjustment: e.target.value })} data-testid="vendor-est-adjust" /></div>
            </div>
            <div className="text-xs text-slate-600">If work proceeds, set adjustment = visiting fee so the ₹150 is not charged twice. For a walk-away, set adjustment to 0 and labour to 0.</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Labour (₹)</Label><Input value={est.labour} onChange={(e) => setEst({ ...est, labour: e.target.value })} data-testid="vendor-est-labour" /></div>
              <div><Label>Taxes (₹)</Label><Input value={est.taxes} onChange={(e) => setEst({ ...est, taxes: e.target.value })} data-testid="vendor-est-taxes" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Materials (pass-through)</Label>
                <Button variant="outline" size="sm" onClick={addMaterial} data-testid="vendor-est-add-material"><Plus className="h-4 w-4 mr-1" />Add</Button>
              </div>
              {est.materials.map((m, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_100px_32px] gap-2 mt-2">
                  <Input placeholder="Name" value={m.name} onChange={(e) => updMaterial(i, "name", e.target.value)} data-testid={`vendor-est-mat-name-${i}`} />
                  <Input placeholder="Qty" value={m.qty} onChange={(e) => updMaterial(i, "qty", e.target.value)} data-testid={`vendor-est-mat-qty-${i}`} />
                  <Input placeholder="Unit price" value={m.unit_price} onChange={(e) => updMaterial(i, "unit_price", e.target.value)} data-testid={`vendor-est-mat-price-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => rmMaterial(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Textarea placeholder="Notes to customer" value={est.notes} onChange={(e) => setEst({ ...est, notes: e.target.value })} className="h-16" data-testid="vendor-est-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEstDialog(false)}>Cancel</Button>
            <Button onClick={postEstimate} data-testid="vendor-est-submit">Post estimate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
